import { User } from '#server/models';
import { Loader, Spinner } from '@/components/Common';
import { Input } from '@/components/ui';
import { SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CACHE_TIME } from '@/constants';
import { trpc } from '@/data-loaders';
import { useGetAdmins, useHandleError, useHandleSuccess } from '@/hooks';
import { useAwmsStore } from '@/store';
import { Button } from '@curtin-dxp/curtin-ui';
import { CircleCheckBig, UserRoundPlus, UserX } from 'lucide-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

//TODO: refactor this component with AddUser
export const AddAdmin = () => {
  const [enteredStaffId, setEnteredStaffId] = useState<string>('');
  const [validId, setValidId] = useState<boolean>(false);
  const activeYear = useAwmsStore((state) => state.activeYear);
  const sendToastAddAdminSuccess = useHandleSuccess({ title: 'Add admin access' });
  const sendToastRevokeAdminAccessSuccess = useHandleSuccess({ title: 'Revoke admin access' });
  const handleError = useHandleError();
  const lastErrorRef = useRef<any>(null);
  const trpcUtils = trpc.useUtils();

  const {
    data: staffName,
    refetch,
    isFetching,
    error: validateStaffIdError,
  } = trpc.admin.validateStaffId.useQuery(
    { staffId: enteredStaffId },
    {
      cacheTime: CACHE_TIME,
      enabled: false,
      retry: false,
    },
  );

  const handleInvalidateAndRefetch = async () => {
    trpcUtils.admin.getAdmins.invalidate();
    trpcUtils.admin.getAdmins.refetch();
  };

  const { mutate: revokeAdminAccessMutation } = trpc.admin.revokeAdminAccess.useMutation({
    onSuccess: async () => {
      sendToastRevokeAdminAccessSuccess();
      handleInvalidateAndRefetch();
    },
    onError: (error) => {
      handleError([error]);
    },
  });

  const { mutate: addAdminAccess } = trpc.admin.addAdminAccess.useMutation({
    onSuccess: async () => {
      handleInvalidateAndRefetch();
      sendToastAddAdminSuccess();
    },
    onError: (error) => {
      handleError([error]);
    },
  });

  const { data: admins, isFetching: isGetAdminsFetching, error: getAdminsError } = useGetAdmins();
  if (getAdminsError) {
    handleError([getAdminsError]);
  }

  const handleStaffIdChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValidId(false);
    setEnteredStaffId(event.target.value);
  };

  const handleValidateStaffId = async () => {
    refetch();
  };

  const handleRevokeAccess = (admin: User) => {
    revokeAdminAccessMutation({
      userId: admin.userId,
      year: activeYear,
    });
  };

  useEffect(() => {
    if (staffName) {
      setValidId(true);
    }
  }, [staffName]);

  useEffect(() => {
    if (validateStaffIdError && validateStaffIdError !== lastErrorRef.current) {
      handleError([validateStaffIdError]);
    }

    lastErrorRef.current = validateStaffIdError;
  }, [handleError, validateStaffIdError]);

  return (
    <>
      {' '}
      <>
        <SheetContent
          className="flex h-auto !max-w-[1250px] flex-col items-start justify-start overflow-y-scroll border-l-0 bg-white p-0"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle className="h-100 flex flex-shrink-0 items-start justify-between self-stretch text-sh3 font-bold text-primary-black-90">
              <div className="flex w-[750px] items-center gap-10 self-stretch py-5 pl-8">Admin users</div>
            </SheetTitle>
            <SheetDescription></SheetDescription>
          </SheetHeader>
          <div className="sticky top-0 w-full bg-white">
            <div className="flex w-[500px] flex-col gap-8 pl-8">
              <div className="flex w-[350px] justify-between">
                <div className="relative">
                  <Input
                    type="text"
                    value={enteredStaffId}
                    className="w-[160px] border-secondary-grey-04 pr-10 text-b2"
                    onChange={handleStaffIdChange}
                    placeholder="Enter Curtin ID"
                  />
                  {validId && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">✔️</span>}
                </div>
                <Button
                  variant="outline-secondary"
                  className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
                    focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
                  aria-label="Validate Curtin ID"
                  onClick={handleValidateStaffId}
                >
                  Validate ID
                  <CircleCheckBig className="ml-[10px] h-6 w-6" />
                </Button>
              </div>
              {validId && (
                <div className="text-sh3">
                  {staffName?.familyName}, {staffName?.firstGivenName}
                </div>
              )}
              {isFetching && (
                <span className="flex items-center justify-start">
                  <Spinner />
                </span>
              )}
            </div>
            <div className="mt-8 pl-8">
              <Button
                variant="outline-secondary"
                className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
                  focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
                aria-label="Admin View"
                onClick={() =>
                  addAdminAccess({
                    staffId: enteredStaffId,
                    year: activeYear,
                  })
                }
              >
                Add admin access
                <UserRoundPlus className="ml-[10px] h-6 w-6" />
              </Button>
            </div>
          </div>
          {admins && admins.length > 0 && !isGetAdminsFetching && (
            <div className="ml-8 mt-4">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead>
                  <tr>
                    <th className="border border-gray-300 px-4 py-2">Curtin ID</th>
                    <th className="border border-gray-300 px-4 py-2">Name</th>
                    <th className="border border-gray-300 px-4 py-2">Access Level</th>
                    <th className="border border-gray-300 px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {admins?.map((admin) => {
                    return (
                      <tr key={admin.curtinId} className="border-t">
                        <td className="border-2 px-4 py-2">{admin.curtinId}</td>
                        <td className="border-2 px-4 py-2">
                          {admin.lastName}, {admin.firstName}
                        </td>
                        <td className="border-2 px-4 py-2">ADMIN</td>
                        <td
                          className="cursor-pointer border-2 px-4 py-2 text-b2 font-semibold text-deep-blue underline"
                          onClick={() => handleRevokeAccess(admin)}
                        >
                          Revoke access
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}{' '}
          {isGetAdminsFetching && <Spinner />}
        </SheetContent>
      </>
    </>
  );
};
